import { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth, adminUid, firebaseReady } from "../firebase";
import {
  createNotice,
  deleteNotice,
  NOTICE_LIMITS,
  NOTICE_TYPES,
  subscribeToNotices,
  updateNotice,
  validateNotice,
} from "../noticeService";
import { importLegacyNotices } from "../legacyNoticeMigration";
import "./NoticePage.css";

const EMPTY_FORM = {
  title: "",
  type: "सूचना",
  date: "",
  content: "",
  important: false,
};

function getAuthMessage(error) {
  if (
    error.code === "auth/invalid-credential" ||
    error.code === "auth/user-not-found" ||
    error.code === "auth/wrong-password"
  ) {
    return "इमेल वा पासवर्ड मिलेन। फेरि प्रयास गर्नुहोस्।";
  }
  if (error.code === "auth/too-many-requests")
    return "धेरै पटक प्रयास भयो। केही समयपछि पुनः प्रयास गर्नुहोस्।";
  return "लगइन हुन सकेन। Firebase Authentication सेटिङ जाँच गर्नुहोस्।";
}

export default function AdminNoticesPage() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState("");
  const [migrationMessage, setMigrationMessage] = useState("");

  useEffect(() => {
    if (!firebaseReady || !auth) {
      setAuthLoading(false);
      return undefined;
    }
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthLoading(false);
      if (nextUser && nextUser.uid === adminUid) setAuthError("");
    });
  }, []);

  const isAdmin = Boolean(user && adminUid && user.uid === adminUid);

  useEffect(() => {
    if (!isAdmin) return undefined;
    setLoading(true);
    setLoadError("");
    return subscribeToNotices(
      (nextNotices, metadata) => {
        setNotices(nextNotices);
        setLoading(false);
        setLoadError(
          metadata && metadata.fromCache
            ? "Firestore सर्भरसँग जडान हुन सकेन। इन्टरनेट जाँच गरी पुनः प्रयास गर्नुहोस्।"
            : "",
        );
      },
      (error) => {
        setLoadError(
          error.code === "permission-denied"
            ? "सूचना पढ्ने अनुमति छैन। Firestore Rules मा प्रशासक UID जाँच गर्नुहोस्।"
            : "सूचनाहरू लोड हुन सकेनन्। इन्टरनेट जाँच गरी पुनः प्रयास गर्नुहोस्।",
        );
        setLoading(false);
      },
    );
  }, [isAdmin]);

  async function handleLogin(event) {
    event.preventDefault();
    setAuthError("");
    if (!firebaseReady || !auth) {
      setAuthError(
        "Firebase कन्फिगरेसन पूरा भएको छैन। .env मा परियोजना विवरण राख्नुहोस्।",
      );
      return;
    }
    if (!adminUid) {
      setAuthError(
        "REACT_APP_ADMIN_UID कन्फिगर गरिएको छैन। Auth UID राखेपछि मात्र व्यवस्थापन खुल्छ।",
      );
      return;
    }
    setBusy(true);
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      if (credential.user.uid !== adminUid) {
        await signOut(auth);
        setAuthError("यो खातालाई सूचना व्यवस्थापनको अनुमति छैन।");
      } else {
        setPassword("");
      }
    } catch (error) {
      setAuthError(getAuthMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    if (!auth) return;
    setBusy(true);
    try {
      await signOut(auth);
      setForm(EMPTY_FORM);
      setEditingId(null);
      setMessage("");
    } catch {
      setAuthError("लगआउट हुन सकेन। पुनः प्रयास गर्नुहोस्।");
    } finally {
      setBusy(false);
    }
  }

  function updateField(event) {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setMessage("");
  }

  function startEditing(notice) {
    setEditingId(notice.id);
    setForm({
      title: notice.title,
      type: NOTICE_TYPES.includes(notice.type) ? notice.type : "सूचना",
      date: notice.date,
      content: notice.content,
      important: notice.important,
    });
    setFieldErrors({});
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEditing() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setMessage("");
  }

  async function handleSave(event) {
    event.preventDefault();
    const { errors, value } = validateNotice(form);
    setFieldErrors(errors);
    setMessage("");
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      if (editingId) await updateNotice(editingId, value, user);
      else await createNotice(value, user);
      setMessage(
        editingId
          ? "सूचना सफलतापूर्वक अद्यावधिक भयो।"
          : "नयाँ सूचना प्रकाशित भयो।",
      );
      setForm(EMPTY_FORM);
      setEditingId(null);
      setFieldErrors({});
    } catch (error) {
      setMessage(
        error.code === "permission-denied"
          ? "यो कार्यको अनुमति छैन। Firestore Rules मा प्रशासक UID जाँच गर्नुहोस्।"
          : "सूचना सुरक्षित हुन सकेन। इन्टरनेट जाँच गरेर फेरि प्रयास गर्नुहोस्। फारमको विवरण यथावत् राखिएको छ।",
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(notice) {
    if (!window.confirm("के तपाईं यो सूचना स्थायी रूपमा मेट्न चाहनुहुन्छ?"))
      return;
    setMessage("");
    setBusy(true);
    try {
      await deleteNotice(notice.id);
      setMessage("सूचना सफलतापूर्वक मेटियो।");
      if (editingId === notice.id) cancelEditing();
    } catch (error) {
      setMessage(
        error.code === "permission-denied"
          ? "यो सूचना मेट्ने अनुमति छैन।"
          : "सूचना मेट्न सकिएन। फेरि प्रयास गर्नुहोस्।",
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    if (
      !window.confirm(
        "पुराना तीन सूचना Firestore मा आयात गर्ने? पहिलेबाट रहेका अभिलेखहरू परिवर्तन गरिने छैनन्।",
      )
    )
      return;
    setMigrationMessage("");
    setBusy(true);
    try {
      const result = await importLegacyNotices(user);
      setMigrationMessage(
        result.alreadyDone
          ? "यो нэгपटकको आयात पहिले नै पूरा भइसकेको छ। कुनै अभिलेख परिवर्तन भएन।"
          : `${result.imported} नयाँ सूचना आयात भयो; ${result.skipped} पहिलेबाट रहेका स्थिर ID का सूचना छोडिए।`,
      );
    } catch (error) {
      setMigrationMessage(
        error.code === "permission-denied"
          ? "आयात अनुमति अस्वीकार भयो। UID सहितका Firestore Rules प्रकाशित छन् कि जाँच गर्नुहोस्। कुनै आंशिक परिवर्तन गरिएको छैन।"
          : "आयात असफल भयो। Firestore जडान जाँच गरेर पुनः प्रयास गर्नुहोस्। कुनै आंशिक परिवर्तन गरिएको छैन।",
      );
    } finally {
      setBusy(false);
    }
  }

  if (authLoading)
    return (
      <main className="admin-page">
        <p className="notice-state" role="status">
          प्रशासक सत्र जाँच हुँदैछ…
        </p>
      </main>
    );

  if (!firebaseReady) {
    return (
      <main className="admin-page">
        <div className="admin-panel">
          <h1>सूचना व्यवस्थापन</h1>
          <p className="notice-error" role="alert">
            Firebase कन्फिगरेसन पूरा छैन। परियोजनाका Web App मानहरू .env मा
            राखेर विकास सर्भर पुनः सुरु गर्नुहोस्।
          </p>
        </div>
      </main>
    );
  }

  if (!user || !isAdmin) {
    return (
      <main className="admin-page">
        <section className="admin-panel">
          <h1>प्रशासक लगइन</h1>
          {user && !isAdmin ? (
            <div className="notice-error" role="alert">
              <p>
                {adminUid
                  ? "यो खातालाई सूचना व्यवस्थापनको अनुमति छैन।"
                  : "REACT_APP_ADMIN_UID कन्फिगर नगरिएकाले व्यवस्थापन बन्द छ।"}
              </p>
              <button
                type="button"
                className="btn-cancel"
                onClick={handleLogout}
                disabled={busy}
              >
                लगआउट
              </button>
            </div>
          ) : (
            <form className="admin-login-form" onSubmit={handleLogin}>
              <label className="form-field">
                <span>इमेल</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <label className="form-field">
                <span>पासवर्ड</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
              {authError && (
                <p className="form-message error" role="alert">
                  {authError}
                </p>
              )}
              <button
                className="btn-primary"
                type="submit"
                disabled={busy || !adminUid}
              >
                {busy ? "प्रमाणीकरण हुँदैछ…" : "लगइन"}
              </button>
              {!adminUid && (
                <p className="form-hint">
                  पहिले .env मा प्रशासकको Firebase Authentication UID
                  राख्नुहोस्।
                </p>
              )}
            </form>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <div className="admin-heading">
        <div>
          <h1>सूचना व्यवस्थापन</h1>
          <p>{user.email}</p>
        </div>
        <button
          type="button"
          className="btn-cancel"
          onClick={handleLogout}
          disabled={busy}
        >
          लगआउट
        </button>
      </div>
      {authError && (
        <p className="notice-error" role="alert">
          {authError}
        </p>
      )}

      <section className="admin-panel">
        <h2>{editingId ? "सूचना सम्पादन" : "नयाँ सूचना"}</h2>
        <form
          className="notice-form admin-notice-form"
          onSubmit={handleSave}
          noValidate
        >
          <div className="form-grid">
            <label className="form-field form-field-full">
              <span>शीर्षक</span>
              <input
                name="title"
                maxLength={NOTICE_LIMITS.title}
                value={form.title}
                onChange={updateField}
                aria-invalid={Boolean(fieldErrors.title)}
                aria-describedby={fieldErrors.title ? "title-error" : undefined}
              />
              {fieldErrors.title && (
                <small id="title-error" className="field-error">
                  {fieldErrors.title}
                </small>
              )}
            </label>
            <label className="form-field">
              <span>सूचनाको प्रकार</span>
              <select name="type" value={form.type} onChange={updateField}>
                {NOTICE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {fieldErrors.type && (
                <small className="field-error">{fieldErrors.type}</small>
              )}
            </label>
            <label className="form-field">
              <span>नेपाली मिति</span>
              <input
                name="date"
                maxLength={NOTICE_LIMITS.date}
                value={form.date}
                onChange={updateField}
                aria-invalid={Boolean(fieldErrors.date)}
                aria-describedby={fieldErrors.date ? "date-error" : undefined}
              />
              {fieldErrors.date && (
                <small id="date-error" className="field-error">
                  {fieldErrors.date}
                </small>
              )}
            </label>
            <label className="form-field form-field-full">
              <span>पूरा विवरण</span>
              <textarea
                name="content"
                rows="7"
                maxLength={NOTICE_LIMITS.content}
                value={form.content}
                onChange={updateField}
                aria-invalid={Boolean(fieldErrors.content)}
                aria-describedby={
                  fieldErrors.content ? "content-error" : undefined
                }
              />
              {fieldErrors.content && (
                <small id="content-error" className="field-error">
                  {fieldErrors.content}
                </small>
              )}
            </label>
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="important"
                checked={form.important}
                onChange={updateField}
              />
              <span>महत्त्वपूर्ण सूचना</span>
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-primary" type="submit" disabled={busy}>
              {busy
                ? "सुरक्षित हुँदैछ…"
                : editingId
                  ? "परिवर्तन सुरक्षित गर्नुहोस्"
                  : "सूचना प्रकाशित गर्नुहोस्"}
            </button>
            {editingId && (
              <button
                className="btn-cancel"
                type="button"
                onClick={cancelEditing}
                disabled={busy}
              >
                रद्द गर्नुहोस्
              </button>
            )}
          </div>
          {message && (
            <p className="form-message" role="status">
              {message}
            </p>
          )}
        </form>
      </section>

      <section className="admin-panel admin-list-panel">
        <h2>
          प्रकाशित सूचनाहरू{" "}
          <span className="admin-count">{notices.length}</span>
        </h2>
        {loading && (
          <p className="notice-state" role="status">
            सूचनाहरू लोड हुँदैछन्…
          </p>
        )}
        {loadError && (
          <p className="notice-error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && notices.length === 0 && (
          <p className="notice-state">अहिले कुनै सूचना छैन।</p>
        )}
        {!loading && !loadError && notices.length > 0 && (
          <ul className="admin-notice-list">
            {notices.map((notice) => (
              <li className="admin-notice-row" key={notice.id}>
                <div>
                  <strong>{notice.title || "शीर्षक उपलब्ध छैन"}</strong>
                  <span>
                    {notice.type} · {notice.date || "मिति उपलब्ध छैन"}
                  </span>
                </div>
                <div className="admin-row-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => startEditing(notice)}
                    disabled={busy}
                  >
                    सम्पादन
                  </button>
                  <button
                    type="button"
                    className="admin-delete-button"
                    onClick={() => handleDelete(notice)}
                    disabled={busy}
                  >
                    मेटाउनुहोस्
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-panel migration-panel">
        <h2>पुराना सूचनाको एकपटकको आयात</h2>
        <p>
          स्थिर ID भएका मूल तीन सूचना आयात हुन्छन्। पहिलेबाट रहेका कागजात
          परिवर्तन हुँदैनन्। Firestore Transaction असफल भएमा सबै लेखाइ रद्द
          हुन्छ।
        </p>
        <button
          type="button"
          className="btn-cancel"
          onClick={handleImport}
          disabled={busy}
        >
          मूल तीन सूचना आयात गर्नुहोस्
        </button>
        {migrationMessage && (
          <p className="form-message" role="status">
            {migrationMessage}
          </p>
        )}
      </section>
    </main>
  );
}
